import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Archive, ArchiveRestore, Copy, Eye, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, gbp } from "@/lib/api";

const STATUS_FILTERS = [
  ["all", "All"], ["live", "Live"], ["draft", "Drafts"], ["archived", "Archived"],
];

export default function AdminProducts() {
  const [products, setProducts] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => setProducts([]));
  useEffect(() => { load(); }, []);

  const act = async (fn, done) => {
    try {
      await fn();
      toast.success(done);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = (p) => {
    if (!window.confirm(`Permanently delete "${p.name}"? This cannot be undone. If it appears in any orders, deletion will be refused — archive it instead.`)) return;
    act(() => api.delete(`/admin/products/${p.id}`), "Product deleted");
  };

  const filtered = (products || []).filter((p) => {
    if (filter === "live") return p.active && !p.archived;
    if (filter === "draft") return !p.active && !p.archived;
    if (filter === "archived") return p.archived;
    return true;
  });

  return (
    <div data-testid="admin-products-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-serif text-2xl font-bold text-cream-text">Products</h1>
        <Link
          to="/admin/products/new"
          className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-choco hover:bg-gold-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus"
          data-testid="new-product-button"
        >
          <Plus className="h-4 w-4" aria-hidden="true" /> New product
        </Link>
      </div>

      <div className="mt-5 flex gap-2" data-testid="product-status-filters">
        {STATUS_FILTERS.map(([key, label]) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`rounded-full px-4 py-1.5 text-xs font-bold ${filter === key ? "bg-gold text-choco" : "border border-choco-line text-cream-muted hover:text-gold"}`}
            data-testid={`filter-${key}`}
          >
            {label}
          </button>
        ))}
      </div>

      {products === null ? (
        <p className="mt-8 text-sm text-cream-muted">Loading…</p>
      ) : (
        <div className="mt-6 space-y-4" data-testid="admin-products-list">
          {filtered.length === 0 && (
            <p className="rounded-3xl border border-choco-line bg-choco-light p-8 text-center text-sm text-cream-muted" data-testid="no-products-message">
              No products here yet.
            </p>
          )}
          {filtered.map((p) => (
            <div key={p.id} className="flex flex-col gap-4 rounded-3xl border border-choco-line bg-choco-light p-5 sm:flex-row sm:items-start" data-testid={`admin-product-row-${p.slug}`}>
              {p.images?.[0] ? (
                <img src={p.images[0].url_400 || p.images[0].url} alt="" className="h-20 w-20 shrink-0 rounded-2xl object-cover" />
              ) : (
                <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-choco text-[10px] text-cream-muted">No image</div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="font-serif text-lg font-semibold text-cream-text">{p.name}</h2>
                  {p.archived ? (
                    <span className="rounded-full bg-choco px-2.5 py-0.5 text-[11px] font-bold text-cream-muted">Archived</span>
                  ) : p.active ? (
                    <span className="rounded-full bg-gold/20 px-2.5 py-0.5 text-[11px] font-bold text-gold">Live</span>
                  ) : (
                    <span className="rounded-full bg-choco px-2.5 py-0.5 text-[11px] font-bold text-cream-muted">Draft</span>
                  )}
                  {p.sold_out && <span className="rounded-full bg-red-500/20 px-2.5 py-0.5 text-[11px] font-bold text-red-300">Sold out</span>}
                  {p.featured && <span className="rounded-full bg-gold/20 px-2.5 py-0.5 text-[11px] font-bold text-gold">Featured</span>}
                  {!p.archived && (p.allergen_information_verified ? (
                    <span className="rounded-full bg-green-500/15 px-2.5 py-0.5 text-[11px] font-bold text-green-400" data-testid={`verified-${p.slug}`}>
                      Food info verified{p.allergen_verified_by ? ` · ${p.allergen_verified_by}` : ""}{p.allergen_verified_at ? ` · ${new Date(p.allergen_verified_at).toLocaleDateString("en-GB")}` : ""}
                    </span>
                  ) : (
                    <span className="rounded-full bg-amber-500/15 px-2.5 py-0.5 text-[11px] font-bold text-amber-300" data-testid={`blocked-${p.slug}`}>
                      Needs allergen review — customers can't order it yet
                    </span>
                  ))}
                </div>
                <p className="mt-1 text-sm text-cream-muted">
                  {p.category_slug} · {gbp(p.price_pence)}
                  {p.variants?.length > 0 && ` · ${p.variants.length} variation${p.variants.length === 1 ? "" : "s"}`}
                </p>
                {p.warnings?.length > 0 && (
                  <ul className="mt-2 space-y-1" data-testid={`warnings-${p.slug}`}>
                    {p.warnings.map((w, i) => (
                      <li key={i} className="text-xs font-semibold text-amber-400">⚠ {w}</li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="flex shrink-0 flex-wrap gap-2">
                <Link to={`/admin/products/${p.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-choco-line px-3.5 py-1.5 text-xs font-bold text-cream-text hover:border-gold hover:text-gold" data-testid={`edit-${p.slug}`}>
                  <Pencil className="h-3.5 w-3.5" aria-hidden="true" /> Edit
                </Link>
                <Link to={`/admin/products/${p.id}/preview`} className="inline-flex items-center gap-1.5 rounded-full border border-choco-line px-3.5 py-1.5 text-xs font-bold text-cream-text hover:border-gold hover:text-gold" data-testid={`preview-${p.slug}`}>
                  <Eye className="h-3.5 w-3.5" aria-hidden="true" /> Preview
                </Link>
                <button onClick={() => act(() => api.post(`/admin/products/${p.id}/duplicate`), "Duplicated as a draft")} className="inline-flex items-center gap-1.5 rounded-full border border-choco-line px-3.5 py-1.5 text-xs font-bold text-cream-text hover:border-gold hover:text-gold" data-testid={`duplicate-${p.slug}`}>
                  <Copy className="h-3.5 w-3.5" aria-hidden="true" /> Duplicate
                </button>
                {p.archived ? (
                  <button onClick={() => act(() => api.post(`/admin/products/${p.id}/reactivate`), "Product reactivated")} className="inline-flex items-center gap-1.5 rounded-full border border-choco-line px-3.5 py-1.5 text-xs font-bold text-cream-text hover:border-gold hover:text-gold" data-testid={`reactivate-${p.slug}`}>
                    <ArchiveRestore className="h-3.5 w-3.5" aria-hidden="true" /> Reactivate
                  </button>
                ) : (
                  <button onClick={() => act(() => api.post(`/admin/products/${p.id}/archive`), "Product archived")} className="inline-flex items-center gap-1.5 rounded-full border border-choco-line px-3.5 py-1.5 text-xs font-bold text-cream-text hover:border-gold hover:text-gold" data-testid={`archive-${p.slug}`}>
                    <Archive className="h-3.5 w-3.5" aria-hidden="true" /> Archive
                  </button>
                )}
                <button onClick={() => remove(p)} className="inline-flex items-center gap-1.5 rounded-full border border-red-500/40 px-3.5 py-1.5 text-xs font-bold text-red-400 hover:bg-red-500/10" data-testid={`delete-${p.slug}`}>
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
